import React, { FC, useEffect } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import {
  Box,
  Code,
  Text,
  Link,
  Flex,
  ButtonClean,
  Modal,
  ButtonIcon
} from '../../../components'
import { PREVIEW_VARS } from 'shared/preview'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import { useClipboard } from 'use-clipboard-copy'
import styled from '@emotion/styled'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { brandColors } from 'shared/basicPalette'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import IconCopy from 'front-end/src/components/common/icon/IconCopy'

export const TemplatingInfoBar: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { featureFlags } = useFeatureFlags()

  if (
    featureFlags?.campaign_templating ||
    featureFlags?.campaign_manager_notification // this feature doesn't make much sense without having email vars
  ) {
    // TODO: introduce useModal(ModalComp) hook which will:
    // - render conditionally, and using portal so it wont clutter devtools
    // - return fn() -> Promise so we can easily chain save/update
    return (
      <Box py="4px">
        <TemplateInfoModal show={isOpen} onClose={onClose} />
        <Box fontSize={fontSizes.sm} color={brandColors.grey.mid}>
          <Trans>You can use some personalisation tags such as</Trans>
          <Text as="span" ml="4px">
            <code>{'[person_first_name]'}</code>,{' '}
            <code>{'[person_email]'}</code>.
          </Text>
        </Box>
        <Link
          fontSize={fontSizes.sm}
          onClick={(e) => (e.preventDefault(), onOpen())}
        >
          <Trans>View personalisation tags</Trans>
        </Link>
      </Box>
    )
  }
  return null
}

const TemplateInfoModal = ({ show, onClose }) => {
  const varEntries = Object.entries(PREVIEW_VARS)

  return (
    <Modal
      show={show}
      header={t`Personalisation tags`}
      subHeader={t`You can use these tags to personalise your message`}
      size="WIDE"
      controls={
        <ButtonClean onClick={onClose}>
          <Trans>Close</Trans>
        </ButtonClean>
      }
      onHide={onClose}
    >
      <Box
        flexWrap="wrap"
        spacing={4}
        align="center"
        maxH="60vh"
        overflow="scroll"
      >
        <VariablesTable entries={varEntries} />
      </Box>
    </Modal>
  )
}

const TrWithHoverableIconButton = styled.tr`
  .IconButton {
    transition: opacity 0.5s;
    opacity: 0;
  }
  &:hover {
    .IconButton {
      opacity: 1;
    }
  }

  code {
    background-color: #edf2f7;
  }
`

const VariablesTable = ({ entries, ...rest }) => {
  const clipboard = useClipboard()

  useEffect(() => {
    const onCopy = (e: ClipboardEvent): void => {
      const text_only = document.getSelection()?.toString()
      const clipboardData = e.clipboardData || (window as any).clipboardData
      clipboardData.setData('text/plain', text_only)
      clipboardData.setData('text/html', text_only)
      e.preventDefault()
    }
    document.addEventListener('copy', onCopy)

    return () => {
      document.removeEventListener('copy', onCopy)
    }
  }, [])

  return (
    <Box flex="1" mb="2" {...rest}>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Tag</Trans>
            </th>
            <th>
              <Trans>Example</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([varName, value]) => {
            const tokenInBrackets = `[${varName}]`
            return (
              <TrWithHoverableIconButton key={varName}>
                <th>
                  <Flex>
                    <Code mr={2} lineHeight="24px">
                      {tokenInBrackets}
                    </Code>
                    <ButtonIcon
                      hint={t`Copy to clipboard`}
                      aria-label={t`Copy to clipboard`}
                      onClick={() => {
                        clipboard.copy(tokenInBrackets)
                        notification.info(t`Copied`)
                      }}
                      icon={
                        <IconCopy
                          color={brandColors.grey[500]}
                          boxSize="16px"
                        />
                      }
                    />
                  </Flex>
                </th>
                <td>
                  <Text>{value}</Text>
                </td>
              </TrWithHoverableIconButton>
            )
          })}
        </tbody>
      </table>
    </Box>
  )
}
